import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Trash2, IndianRupee } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PaymentStatusBadge, fmtRs } from './PaymentStatusBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const MODES = ['Cash', 'NEFT/RTGS', 'UPI', 'Cheque', 'Other'];
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const dmy = (d) => (d ? d.slice(0, 10).split('-').reverse().join('-') : '—');
const blank = () => ({ payment_date: today(), amount: '', mode: 'NEFT/RTGS', reference: '', notes: '', allow_overpayment: false, overpayment_reason: '' });

const Stat = ({ label, value, tone = 'text-slate-900', testId }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
    <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`text-lg font-bold ${tone}`} data-testid={testId}>{value}</p>
  </div>
);

const History = ({ rows, isAdmin, onDelete }) => (
  <div className="max-h-56 overflow-auto rounded-lg border border-slate-200">
    <table className="w-full text-sm" data-testid="grn-payment-history">
      <thead className="sticky top-0 bg-slate-100 text-xs uppercase text-slate-600">
        <tr><th className="p-2 text-left">Date</th><th className="p-2 text-right">Amount</th><th className="p-2 text-left">Mode</th>
          <th className="p-2 text-left">Reference</th><th className="p-2 text-left">Notes</th><th className="p-2 text-left">By</th>{isAdmin && <th />}</tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-slate-400">No payments recorded yet</td></tr>}
        {rows.map((p) => (
          <tr key={p.id} className="border-t border-slate-100" data-testid={`grn-payment-row-${p.id}`}>
            <td className="p-2">{dmy(p.payment_date)}</td>
            <td className="p-2 text-right font-medium">{fmtRs(p.amount)}{p.is_overpayment && <span className="ml-1 block text-[10px] text-amber-600" title={p.overpayment_reason}>incl. {fmtRs(p.amount - (p.outstanding_before || 0))} advance</span>}</td>
            <td className="p-2">{p.mode}</td><td className="p-2 font-mono text-xs">{p.reference || '—'}</td>
            <td className="p-2 text-xs text-slate-600">{p.notes || p.overpayment_reason || '—'}</td>
            <td className="p-2 text-xs text-slate-500">{p.created_by_name || p.created_by}</td>
            {isAdmin && <td className="p-2"><Button size="sm" variant="ghost" onClick={() => onDelete(p)} data-testid={`grn-payment-delete-${p.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button></td>}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const GRNPaymentsDialog = ({ grn, open, onClose, onChanged, isAdmin }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => grn && axios.get(`${API}/grn/${grn.id}/payments`, auth()).then((r) => setData(r.data))
    .catch(() => toast.error('Could not load payments'));
  useEffect(() => { if (open) { setData(null); setForm(blank()); load(); } }, [open, grn?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const amt = parseFloat(form.amount) || 0;
  const over = data && amt - data.outstanding_amount > 0.01;
  const done = (d, msg) => { setData(d); setForm(blank()); onChanged(d); toast.success(msg); };

  const save = async () => {
    if (amt <= 0) return toast.error('Enter an amount greater than 0');
    if (over && !form.allow_overpayment) return toast.error('Amount is more than the outstanding balance');
    if (over && !form.overpayment_reason.trim()) return toast.error('Give a reason for the overpayment');
    setSaving(true);
    try {
      const r = await axios.post(`${API}/grn/${grn.id}/payments`, { ...form, amount: amt }, auth());
      done(r.data, 'Payment recorded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save payment'); } finally { setSaving(false); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete payment of ${fmtRs(p.amount)} dated ${dmy(p.payment_date)}?`)) return;
    try { done((await axios.delete(`${API}/grn/${grn.id}/payments/${p.id}`, auth())).data, 'Payment deleted'); } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-white" data-testid="grn-payments-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2"><IndianRupee className="h-5 w-5 text-emerald-600" />Payments · {grn?.grn_number}
            {data && <PaymentStatusBadge status={data.payment_status} testId="grn-payment-dialog-status" />}</DialogTitle>
          <p className="text-sm text-slate-500">{grn?.supplier_name} · Invoice {grn?.invoice_number || '—'}</p>
        </DialogHeader>
        {!data ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Stat label="GRN Value" value={fmtRs(data.grn_value)} testId="grn-pay-value" />
              <Stat label={`Total Paid · ${data.grn_value ? Math.round(data.amount_paid / data.grn_value * 1000) / 10 : 0}%`} value={fmtRs(data.amount_paid)} tone="text-emerald-700" testId="grn-pay-paid" />
              <Stat label="Outstanding" value={fmtRs(data.outstanding_amount)} tone={data.outstanding_amount > 0 ? 'text-red-600' : 'text-slate-900'} testId="grn-pay-outstanding" />
              <Stat label="Advance" value={fmtRs(data.advance_amount)} tone="text-amber-600" testId="grn-pay-advance" />
            </div>
            <History rows={data.payments} isAdmin={isAdmin} onDelete={del} />
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="grn-payment-form">
              <p className="mb-2 text-sm font-semibold text-slate-800">{data.amount_paid > 0 ? 'Record additional payment' : 'Record payment'}</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
                <div><Label className="text-xs">Date</Label><Input className="bg-white" type="date" value={form.payment_date} onChange={(e) => set('payment_date', e.target.value)} data-testid="grn-pay-date" /></div>
                <div><Label className="text-xs">Amount (₹)</Label><Input className="bg-white" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => set('amount', e.target.value)} data-testid="grn-pay-amount" />
                  {data.outstanding_amount > 0 && <button type="button" className="mt-0.5 text-[11px] text-emerald-700 hover:underline" onClick={() => set('amount', String(data.outstanding_amount))} data-testid="grn-pay-fill-outstanding">Pay full {fmtRs(data.outstanding_amount)}</button>}</div>
                <div><Label className="text-xs">Mode</Label>
                  <Select value={form.mode} onValueChange={(v) => set('mode', v)}><SelectTrigger data-testid="grn-pay-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs">Reference / UTR</Label><Input className="bg-white" value={form.reference} onChange={(e) => set('reference', e.target.value)} data-testid="grn-pay-reference" /></div>
              </div>
              <div className="mt-2"><Label className="text-xs">Notes</Label><Input className="bg-white" value={form.notes} onChange={(e) => set('notes', e.target.value)} data-testid="grn-pay-notes" /></div>
              {over && (
                <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-800" data-testid="grn-pay-over-warning">
                  {fmtRs(amt)} is more than the outstanding {fmtRs(data.outstanding_amount)}.
                  {isAdmin ? (
                    <div className="mt-1 space-y-1">
                      <label className="flex items-center gap-2"><Checkbox checked={form.allow_overpayment} onCheckedChange={(v) => set('allow_overpayment', !!v)} data-testid="grn-pay-allow-over" />Allow overpayment (extra is kept as advance)</label>
                      {form.allow_overpayment && <Input className="bg-white" placeholder="Reason (required)" value={form.overpayment_reason} onChange={(e) => set('overpayment_reason', e.target.value)} data-testid="grn-pay-over-reason" />}
                    </div>
                  ) : <span> Only an admin can allow an overpayment.</span>}
                </div>
              )}
              <div className="mt-3 flex justify-end"><Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="grn-pay-save">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save Payment</Button></div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GRNPaymentsDialog;
